import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBarcode,
  faCamera,
  faChevronDown,
  faChevronUp,
  faCookie,
  faDroplet,
  faFire,
  faLeaf,
  faMountain,
  faPills,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import {
  buildInitialMacroValues,
} from './foodFormUtils';

const PHOTO_MAX_SIZE_MB = 8;

const PHOTO_HINTS = {
  front: 'Produit entier, bien cadré, sans reflet',
  ingredients: 'Liste complète, nette et lisible',
  nutrition: 'Tableau des valeurs nutritionnelles entier',
  packaging: 'Dos ou tranche avec infos complémentaires',
};

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  if (!file) {
    resolve(null);
    return;
  }

  const reader = new FileReader();
  reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
  reader.onerror = () => reject(reader.error || new Error('Impossible de lire l\'image.'));
  reader.readAsDataURL(file);
});

const buildImagePayload = async (images) => {
  const entries = await Promise.all(
    Object.entries(images)
      .filter(([, file]) => file)
      .map(async ([field, file]) => [field, await readFileAsDataUrl(file)])
  );

  return Object.fromEntries(entries.filter(([, value]) => value));
};

/** Collapsible macro section for new-product mode */
const MacroSection = ({ title, subtitle, icon, fieldCount, children, defaultOpen = false, iconStyle }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      border: '0.5px solid #D1D5DB',
      borderRadius: '10px',
      marginBottom: '8px',
      overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', background: open ? '#F3F4F6' : '#fff',
          border: 'none', width: '100%', cursor: 'pointer', gap: '10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '30px', height: '30px', borderRadius: '8px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '15px', flexShrink: 0, ...iconStyle,
          }}>
            <FontAwesomeIcon icon={icon} />
          </div>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1A2E40' }}>{title}</div>
            {subtitle && <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '1px' }}>{subtitle}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{
            fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '99px',
            background: open ? '#E1F5EE' : '#F3F4F6',
            color: open ? '#0F6E56' : '#6B7280',
          }}>
            {fieldCount} champ{fieldCount > 1 ? 's' : ''}
          </span>
          <FontAwesomeIcon
            icon={open ? faChevronUp : faChevronDown}
            style={{ fontSize: '13px', color: '#9CA3AF' }}
          />
        </div>
      </button>

      {open && (
        <div style={{
          borderTop: '0.5px solid #E5E7EB',
          padding: '12px 14px',
          background: '#F9FAFB',
        }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ─── main component ─────────────────────────────────────────────────────────

const EditFoodForm = ({
  onSubmit,
  isMobileViewport = false,
  initialData = null,
  isEditMode = false,
}) => {
  // new-product form fields
  const [barcode, setBarcode] = useState(initialData?.barcode || '');
  const [newName, setNewName] = useState(initialData?.name || '');
  const [newBrand, setNewBrand] = useState(initialData?.brand || '');
  const [newQuantity, setNewQuantity] = useState(initialData?.quantity || 100);
  const [newIngredients, setNewIngredients] = useState(initialData?.ingredients || []);
  const [macroValues, setMacroValues] = useState(
    buildInitialMacroValues(initialData?.macronutrients || {})
  );
  const [images, setImages] = useState({
    front: null,
    ingredients: null,
    nutrition: null,
    packaging: null,
  });
  const [ingredientInput, setIngredientInput] = useState('');

  const handleBarcodeChange = (e) => {
    const val = e.target.value;
    setBarcode(val);
  };

  const [imagePreviews, setImagePreviews] = useState({});
  const [imageErrors, setImageErrors] = useState({});

  // Génère les aperçus et évite la fuite mémoire des blob URLs
  useEffect(() => {
    const urls = {};
    Object.entries(images).forEach(([key, file]) => {
      if (file) urls[key] = URL.createObjectURL(file);
    });
    setImagePreviews(urls);
    return () => {
      Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [images]);

  const handleImageChange = (key) => (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setImageErrors((prev) => ({ ...prev, [key]: 'Format non pris en charge (JPG ou PNG)' }));
      return;
    }
    if (file.size > PHOTO_MAX_SIZE_MB * 1024 * 1024) {
      setImageErrors((prev) => ({ ...prev, [key]: `Trop lourd — max ${PHOTO_MAX_SIZE_MB} Mo` }));
      return;
    }
    setImageErrors((prev) => ({ ...prev, [key]: null }));
    setImages((prev) => ({ ...prev, [key]: file }));
  };

  // ── submit ─────────────────────────────────────────────────────────────
  const handleSubmit = async (event) => {
    event.preventDefault();

    const imagePayload = await buildImagePayload(images);

    onSubmit({
      barcode: barcode.trim(),
      name: newName.trim(),
      brand: newBrand.trim(),
      quantity: Number(newQuantity) || 100,
      macronutrients: macroValues,
      ingredients: newIngredients,
      images: imagePayload,
      openFoodFactsData: null,
      _offExists: false,
      _newBrand: newBrand.trim(),
    });
  };

  const inputStyle = {
    width: '100%',
    padding: '0.65rem 0.75rem',
    border: '1.5px solid #D1D5DB',
    borderRadius: '8px',
    fontSize: isMobileViewport ? '16px' : '0.92rem',
    fontFamily: 'Roboto, sans-serif',
    color: '#1A2E40',
    background: '#F9FAFB',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontWeight: 700,
    fontSize: '0.83rem',
    color: '#1A2E40',
    marginBottom: '0.3rem',
  };

  return (
    <>
      <form id="edit-food-form" onSubmit={handleSubmit} className="food-form-improved">

        {/* ── Step 1: barcode entry ── */}
        <div className="form-group" style={{ marginBottom: '0.75rem' }}>
          <label style={labelStyle}>
            <FontAwesomeIcon icon={faBarcode} style={{ marginRight: '0.4rem', color: '#6c757d' }} />
            Code-barres EAN
          </label>
          <div style={{ display: 'flex', gap: '0.45rem', alignItems: 'stretch' }}>
            <input
              type="text"
              inputMode="numeric"
              value={barcode}
              onChange={handleBarcodeChange}
              placeholder="Ex: 3274080005003"
              disabled
              style={{ ...inputStyle, flex: 1, backgroundColor: '#E5E7EB' }}
            />
          </div>
        </div>

        {/* ── Manual product entry form ── */}
        <div
          style={{
            background: isEditMode ? '#E3F2FD' : '#FFF8E1',
            border: isEditMode ? '1px solid #90CAF9' : '1px solid #FFE082',
            borderRadius: '8px',
            padding: '0.6rem 0.85rem',
            fontSize: '0.82rem',
            color: isEditMode ? '#1565C0' : '#5D4037',
            fontWeight: 600,
            marginBottom: '0.85rem',
          }}
        >
          {isEditMode
            ? 'Modification d\'un produit existant sur OpenFoodFacts — Les modifications seront appliquées à la base de données.'
            : 'Nouveau produit — Les informations que tu saisis seront proposées à OpenFoodFacts.'}
        </div>

        <div className="form-group">
          <label style={labelStyle}>Nom de l'aliment *</label>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Ex: Poulet rôti, Yaourt nature…"
            required
            style={inputStyle}
          />
        </div>

        <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div className="form-group">
            <label style={labelStyle}>Marque (optionnelle)</label>
            <input
              type="text"
              value={newBrand}
              onChange={(e) => setNewBrand(e.target.value)}
              placeholder="Ex: Danone"
              style={inputStyle}
            />
          </div>
          <div className="form-group">
            <label style={labelStyle}>Quantité (g)</label>
            <input
              type="number"
              min={1}
              max={9999}
              value={newQuantity}
              onChange={(e) => setNewQuantity(e.target.value)}
              required
              style={inputStyle}
            />
          </div>
        </div>

        {/* Macros: primary — open by default */}
        <MacroSection
          title="Macronutriments principaux"
          subtitle="pour 100 g"
          icon={faFire}
          iconStyle={{ background: '#E1F5EE', color: '#0F6E56' }}
          fieldCount={4}
          defaultOpen
        >
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
            {[
              { label: 'Calories (kcal)', key: 'energy-kcal_100g', step: '1' },
              { label: 'Protéines (g)', key: 'proteins_100g', step: '0.1' },
              { label: 'Glucides (g)', key: 'carbohydrates_100g', step: '0.1' },
              { label: 'Lipides (g)', key: 'fat_100g', step: '0.1' },
            ].map(({ label, key, step }) => (
              <div key={key} className="form-group">
                <label style={labelStyle}>{label}</label>
                <input type="number" min={0} step={step} value={macroValues[key]}
                  onChange={(e) => setMacroValues((prev) => ({ ...prev, [key]: e.target.value }))}
                  style={inputStyle} />
              </div>
            ))}
          </div>
        </MacroSection>

        {/* Glucides détaillés */}
        <MacroSection
          title="Glucides détaillés"
          subtitle="fibres · sucres · amidon"
          icon={faLeaf}
          iconStyle={{ background: '#FAEEDA', color: '#854F0B' }}
          fieldCount={4}
        >
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
            {[
              { label: 'Fibres (g)', key: 'fiber_100g' },
              { label: 'Sucres (g)', key: 'sugars_100g' },
              { label: 'Sucres ajoutés (g)', key: 'added-sugars_100g' },
              { label: 'Amidon (g)', key: 'starch_100g' },
            ].map(({ label, key }) => (
              <div key={key} className="form-group">
                <label style={labelStyle}>{label}</label>
                <input type="number" min={0} step="0.1" value={macroValues[key]}
                  onChange={(e) => setMacroValues((prev) => ({ ...prev, [key]: e.target.value }))}
                  style={inputStyle} />
              </div>
            ))}
          </div>
        </MacroSection>

        {/* Lipides détaillés */}
        <MacroSection
          title="Lipides détaillés"
          subtitle="saturés · trans · cholestérol"
          icon={faDroplet}
          iconStyle={{ background: '#FAECE7', color: '#993C1D' }}
          fieldCount={5}
        >
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
            {[
              { label: 'AG saturés (g)', key: 'saturated-fat_100g' },
              { label: 'AG trans (g)', key: 'trans-fat_100g' },
              { label: 'AG monoinsaturés (g)', key: 'monounsaturated-fat_100g' },
              { label: 'AG polyinsaturés (g)', key: 'polyunsaturated-fat_100g' },
              { label: 'Cholestérol (mg)', key: 'cholesterol_100g' },
            ].map(({ label, key }) => (
              <div key={key} className="form-group">
                <label style={labelStyle}>{label}</label>
                <input type="number" min={0} step="0.1" value={macroValues[key]}
                  onChange={(e) => setMacroValues((prev) => ({ ...prev, [key]: e.target.value }))}
                  style={inputStyle} />
              </div>
            ))}
          </div>
        </MacroSection>

        {/* Minéraux */}
        <MacroSection
          title="Minéraux"
          subtitle="sodium · calcium · fer…"
          icon={faMountain}
          iconStyle={{ background: '#EEEDFE', color: '#534AB7' }}
          fieldCount={6}
        >
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
            {[
              { label: 'Sodium (g)', key: 'sodium_100g', step: '0.001' },
              { label: 'Sel (g)', key: 'salt_100g', step: '0.01' },
              { label: 'Calcium (mg)', key: 'calcium_100g' },
              { label: 'Fer (mg)', key: 'iron_100g' },
              { label: 'Magnésium (mg)', key: 'magnesium_100g' },
              { label: 'Zinc (mg)', key: 'zinc_100g' },
            ].map(({ label, key, step = '0.1' }) => (
              <div key={key} className="form-group">
                <label style={labelStyle}>{label}</label>
                <input type="number" min={0} step={step} value={macroValues[key]}
                  onChange={(e) => setMacroValues((prev) => ({ ...prev, [key]: e.target.value }))}
                  style={inputStyle} />
              </div>
            ))}
          </div>
        </MacroSection>

        {/* Vitamines */}
        <MacroSection
          title="Vitamines"
          subtitle="vitamine C"
          icon={faPills}
          iconStyle={{ background: '#E6F1FB', color: '#185FA5' }}
          fieldCount={1}
        >
          <div style={{ maxWidth: '50%' }}>
            <div className="form-group">
              <label style={labelStyle}>Vitamine C (mg)</label>
              <input type="number" min={0} step="0.1" value={macroValues['vitamin-c_100g']}
                onChange={(e) => setMacroValues((prev) => ({ ...prev, 'vitamin-c_100g': e.target.value }))}
                style={inputStyle} />
            </div>
          </div>
        </MacroSection>

        {/* Ingrédients */}
        <div className="form-group" style={{ marginTop: '0.85rem' }}>
          <label style={labelStyle}>
            <FontAwesomeIcon icon={faCookie} style={{ marginRight: '0.4rem', color: '#6c757d' }} />
            Ingrédients
          </label>

          {/* Tags */}
          {newIngredients.length > 0 && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '6px',
              marginBottom: '8px',
            }}>
              {newIngredients.map((ing, i) => (
                <span key={i} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                  background: '#F3F4F6', border: '0.5px solid #D1D5DB',
                  borderRadius: '99px', padding: '3px 10px 3px 12px',
                  fontSize: '0.8rem', color: '#1A2E40', fontWeight: 500,
                }}>
                  {ing}
                  <button
                    type="button"
                    onClick={() => setNewIngredients(prev => prev.filter((_, j) => j !== i))}
                    style={{
                      background: '#D1D5DB', border: 'none', borderRadius: '50%',
                      width: '16px', height: '16px', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', cursor: 'pointer', padding: 0,
                      color: '#4B5563', fontSize: '9px', flexShrink: 0,
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Input */}
          <input
            type="text"
            value={ingredientInput}
            onChange={(e) => {
              const val = e.target.value;
              if (val.endsWith(',')) {
                const trimmed = val.slice(0, -1).trim();
                if (trimmed) setNewIngredients(prev => [...prev, trimmed]);
                setIngredientInput('');
              } else {
                setIngredientInput(val);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const trimmed = ingredientInput.trim();
                if (trimmed) setNewIngredients(prev => [...prev, trimmed]);
                setIngredientInput('');
              }
              // Backspace sur input vide → supprime le dernier tag
              if (e.key === 'Backspace' && ingredientInput === '') {
                setNewIngredients(prev => prev.slice(0, -1));
              }
            }}
            placeholder={newIngredients.length === 0 ? 'Ex: Farine de blé, eau, sel…' : 'Ajouter un ingrédient…'}
            style={inputStyle}
          />
          {newIngredients.length > 0 && (
            <small style={{ color: '#6B7280', fontSize: '0.78rem', marginTop: '4px', display: 'block' }}>
              {newIngredients.length} ingrédient{newIngredients.length > 1 ? 's' : ''} · Backspace pour supprimer le dernier
            </small>
          )}
        </div>

        {/* Photos du produit */}
        <div className="form-group" style={{ marginTop: '0.85rem' }}>
          <label style={labelStyle}>
            <FontAwesomeIcon icon={faCamera} style={{ marginRight: '0.4rem', color: '#6c757d' }} />
            Photos du produit (optionnelles)
          </label>
          <small style={{ color: '#6B7280', fontSize: '0.78rem', display: 'block', marginTop: '2px' }}>
            JPG ou PNG · {PHOTO_MAX_SIZE_MB} Mo max par photo
          </small>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem', marginTop: '0.5rem' }}>
            {[
              { key: 'front', label: 'Face avant' },
              { key: 'ingredients', label: 'Ingrédients' },
              { key: 'nutrition', label: 'Nutrition' },
              { key: 'packaging', label: 'Emballage' },
            ].map(({ key, label }) => {
              const file = images[key];
              const previewUrl = imagePreviews[key];
              const error = imageErrors[key];

              return (
                <div key={key}>
                  <label
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.4rem',
                      border: `1.5px dashed ${error ? '#DC2626' : file ? '#1A2E40' : '#D1D5DB'}`,
                      borderRadius: '8px',
                      padding: '0.75rem 0.5rem',
                      background: file ? '#F0F4F8' : '#F9FAFB',
                      cursor: 'pointer',
                      position: 'relative',
                      minHeight: '90px',
                    }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      aria-label={`Ajouter une photo : ${label}`}
                      style={{ display: 'none' }}
                      onChange={handleImageChange(key)}
                    />
                    {previewUrl ? (
                      <>
                        <img
                          src={previewUrl}
                          alt={label}
                          style={{ width: '100%', maxHeight: '70px', objectFit: 'cover', borderRadius: '5px' }}
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setImages((prev) => ({ ...prev, [key]: null }));
                            setImageErrors((prev) => ({ ...prev, [key]: null }));
                          }}
                          style={{
                            position: 'absolute', top: '4px', right: '4px',
                            background: '#1A2E40', color: '#fff', border: 'none',
                            borderRadius: '50%', width: '20px', height: '20px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', fontSize: '0.65rem',
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCamera} style={{ fontSize: '1.25rem', color: '#9CA3AF' }} />
                        <span style={{ fontSize: '0.78rem', color: '#6B7280', fontWeight: 600, textAlign: 'center' }}>
                          {label}
                        </span>
                      </>
                    )}
                  </label>

                  {/* Conseil, taille du fichier ou erreur */}
                  {error ? (
                    <small style={{ color: '#DC2626', fontSize: '0.72rem', display: 'block', marginTop: '3px' }}>
                      {error}
                    </small>
                  ) : file ? (
                    <small style={{ color: '#0F6E56', fontSize: '0.72rem', display: 'block', marginTop: '3px' }}>
                      ✓ {(file.size / (1024 * 1024)).toFixed(1)} Mo
                    </small>
                  ) : (
                    <small style={{ color: '#9CA3AF', fontSize: '0.72rem', display: 'block', marginTop: '3px' }}>
                      {PHOTO_HINTS[key]}
                    </small>
                  )}
                </div>
              );
            })}
          </div>
        </div>

      </form>
    </>
  );
};

export default EditFoodForm;
